import React, { useState, useEffect } from 'react';
import { Save, X } from 'lucide-react';

const SaveCaseModal = ({ isOpen, onClose, onSave, existingCase = null }) => {
  const [caseName, setCaseName] = useState('');
  const [error, setError] = useState('');

  // Update case name when modal opens or existingCase changes
  useEffect(() => {
    if (isOpen) {
      setCaseName(existingCase?.name || '');
      setError('');
    }
  }, [isOpen, existingCase?.name]);

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validation - for existing case, name should already be set
    const nameToSave = existingCase ? existingCase.name : caseName.trim();
    
    if (!nameToSave) {
      setError('Case name/initials is required');
      return;
    }
    
    // Pass name and caseId (if existing case) to parent
    // If no caseId, parent will create new case on server
    onSave(nameToSave, existingCase?.caseId || null);
    
    // Reset form
    setCaseName('');
    setError('');
  };

  const handleClose = () => {
    setCaseName('');
    setError('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">
            {existingCase ? 'Update Case Information' : 'Save Assessment to Case'}
          </h3>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
            title="Close"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Case Name/Initials *
            </label>
            <input
              type="text"
              value={caseName}
              onChange={(e) => {
                setCaseName(e.target.value);
                setError('');
              }}
              placeholder="e.g., JD, John Doe"
              className={`w-full px-4 py-3 border rounded-lg focus:outline-none ${
                existingCase 
                  ? 'border-gray-300 bg-gray-100 text-gray-700 cursor-not-allowed' 
                  : 'border-gray-300 focus:border-teal-500'
              }`}
              autoFocus={!existingCase}
              disabled={!!existingCase}
            />
            {existingCase && (
              <p className="text-xs text-gray-500 mt-1">
                Case ID: {existingCase.caseId}
              </p>
            )}
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              {existingCase ? (
                <>
                  <strong>Note:</strong> This assessment will be saved to case <strong>{existingCase.name}</strong>.
                </>
              ) : (
                <>
                  <strong>Note:</strong> A new case will be created with this name, and this assessment will be saved to their record.
                </>
              )}
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={handleClose}
              className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-6 py-3 bg-teal-600 text-white rounded-lg font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center gap-2"
            >
              <Save size={20} />
              Save Assessment
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SaveCaseModal;

